import { ReactNode, useRef, useState } from 'react';
import { useReward } from '@/contexts/reward-context';
import { replaceResource, getProgressPercentage, addCommas } from '@/utils';
import { Reward, RewardCardProps, RewardType as RewardTypeEnum } from '@/types';
import { Tooltip } from './tooltip';
import { ArrowRightIcon, InfoIcon } from './svg-icons';
import { RewardTasksPeriodCountdown } from '@/components/reward-tasks-period-countdown';

const RewardCard = ({ data }: RewardCardProps) => {
	const { RewardId, RewardName, Events, RewardType } = data;

	const currentEvent = Events.find(event => event.IsCurrentLevel) ?? Events.at(-1);

	const isDisabled = Object.keys(RewardTypeEnum).every(type => RewardTypeEnum[type as keyof typeof RewardTypeEnum] !== RewardType);

	const isShowGoBtn = Events.some(data => data.CompleteDate == null);

	const { labels, setEventListScene, goToEvent, onKafkaSet } = useReward();

	const toEventList = () => {
		let action = 1;
		switch (RewardId) {
			case 1:
				action = 12;
				break;
			case 2:
				action = 13;
				break;
			case 3:
				action = 14;
				break;
			case 6:
				action = 10;
				break;
			case 7:
				action = 11;
				break;
		}
		
		onKafkaSet({
			type: 2,
			action: action
		});

		if (!isDisabled && setEventListScene) {
			setEventListScene(RewardId);
		}
	};

	const toEvent = (event: React.MouseEvent) => {
		event.stopPropagation();
		if (goToEvent && isShowGoBtn) {
			let action = 1;
			switch (RewardId) {
				case 1:
					action = 17;
					break;
				case 2:
					action = 18;
					break;
				case 3:
					action = 19;
					break;
				case 6:
					action = 15;
					break;
				case 7:
					action = 16;
					break;
			}
			
			onKafkaSet({
				type: 2,
				action: action
			});

			goToEvent(data.RewardType, data.RewardId, true);
		}
	}

	const rewardClass = {
		[RewardTypeEnum.Betting]: 'reward-chlg-item--turnover',
		[RewardTypeEnum.Parlay]: 'reward-chlg-item--ticket',
		[RewardTypeEnum.Streamer]: 'reward-chlg-item--counting',
		[RewardTypeEnum.Survey]: 'reward-chlg-item--survey',
		[RewardTypeEnum.BookedBet]: 'reward-chlg-item--fast'
	}[RewardType];

	return (
		<div
			className={`reward-chlg-item ${rewardClass}`}
			onClick={toEventList}
		>
			<i className={{
				[RewardTypeEnum.Betting]: 'reward-iconcolor-img-turnover',
				[RewardTypeEnum.Parlay]: 'reward-iconcolor-img-ticket',
				[RewardTypeEnum.Streamer]: 'reward-iconcolor-img-counting',
				[RewardTypeEnum.Survey]: 'reward-iconcolor-img-survey',
				[RewardTypeEnum.BookedBet]: 'reward-iconcolor-img-fast'
			}[RewardType]}
			/>
			{
				RewardType !== RewardTypeEnum.BookedBet ? null : (
					<div className="reward-chlg-item__tag">
						<div className="reward-chlg-item__tag-bar">{labels?.lbl_RPMemberBenefitsCSJ05}</div>
					</div>
				)
			}
			{
				RewardType !== RewardTypeEnum.BookedBet ? (
					<div className="reward-exp-point">
						<div className="reward-exp-point__icon">
							<i className="reward-iconcolor-exp-star" />
						</div>
						<div className="reward-exp-point__text">{currentEvent?.Exp}</div>
					</div>
				) : (
					<div className="reward-coin">
						<div className="reward-coin__icon">
							<i className="reward-iconcolor-shop-coin"></i>
						</div>
						<div className="reward-coin__text">{100}</div>
					</div>
				)
			}
			<div className="reward-chlg-item__info">
				<div className="reward-chlg-item__title">
					<span className="reward-chlg-item__name">
						{replaceResource(labels?.[RewardName] ?? '', addCommas(currentEvent?.Threshold ?? 0))}
					</span>
					<ArrowRightIcon />
				</div>
				<div className="reward-progress">
					<div className="reward-progress__meter">
						<div
							className="reward-progress__bar"
							style={{
								width: `${getProgressPercentage(currentEvent?.Progress ?? 0, currentEvent?.Threshold ?? 0)}%`
							}}
						/>
					</div>
					<div className="reward-progress__state">
						{`${addCommas(currentEvent?.Progress ?? 0)} / ${addCommas(currentEvent?.Threshold ?? 0)}`}
					</div>
				</div>
			</div>
			<button
				className="reward-btn reward-btn--secondary"
				type="button"
				onClick={toEvent}
				disabled={!isShowGoBtn}
			>
				<div className="reward-btn__info">
					<span className="reward-btn__text">{labels?.lbl_RPGo}</span>
				</div>
				<ArrowRightIcon />
			</button>
		</div>
	)
};

export const RewardSystem = ({ isShow }: { isShow: boolean }) => {
	const [isShowingDetail, setIsShowingDetail] = useState(false);
	const challengeTimerRef = useRef<HTMLDivElement>(null);
	const { labels, planInfo, onKafkaSet, enablePromoteBookedBet } = useReward();

	const toggleToolTip = () => {
		onKafkaSet({
			type: 2,
			action: 9
		});
		setIsShowingDetail((prev) => !prev);
	}

	return (
		<div className="reward-content reward-content--challenge" data-show={isShow}>
			<div className="reward-chlg-head">
				<i className="reward-iconcolor-calendar" />
				<div className="reward-chlg-head__title">{labels?.lbl_RPCycleChallenge}</div>
				<div
					ref={challengeTimerRef}
					className="reward-chlg-head__timer"
					onClick={toggleToolTip}
					data-show={isShowingDetail}
				>
					{planInfo?.PeriodEndDate ? (
						<RewardTasksPeriodCountdown periodEndDate={planInfo.PeriodEndDate} />
					) : null}
					
					<InfoIcon />
				</div>
				<Tooltip
					isShowingDetail={isShowingDetail}
					setIsShowingDetail={setIsShowingDetail}
					triggerRef={challengeTimerRef}
				>
					<div className="reward-tooltip__text">{labels?.lbl_RPTimeRange}</div>
				</Tooltip>
			</div>
			<div className="reward-chlg-list">
				{
					planInfo?.Rewards.filter((rewardData: Reward) => {
						return rewardData.RewardType === RewardTypeEnum.SignIn ? false : rewardData.RewardType === RewardTypeEnum.BookedBet ? enablePromoteBookedBet : true;
					})
						.sort((rewardA, rewardB) => {
							const isRewardABookedBet = rewardA.RewardType === RewardTypeEnum.BookedBet;
							const isRewardBBookedBet = rewardB.RewardType === RewardTypeEnum.BookedBet;
							return (!isRewardABookedBet && !isRewardBBookedBet) ? 0 : isRewardABookedBet ? -1 : 1;
						})
						.map((rewardData: Reward): ReactNode => {
							return (
								<RewardCard
									key={rewardData.RewardId}
									data={rewardData}
								/>
							);
						})
				}
			</div>
		</div>
	)
};
